import { Component, Input, Output, OnChanges, SimpleChanges, EventEmitter, inject } from '@angular/core';
import { PaginatedResponse, BaseComponent, OrderByValue } from '../../../../core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { CERTIFICATION_TYPE, CodeService, CommonCode } from '../../../../shared';
import { Utils, Tree } from '../../../../common';
import { ProductListDto, Brand, Product, DisplayCategory } from '../../../../dto';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { ActivatedRoute } from '@angular/router';
import { takeWhile } from 'rxjs/operators';
import { AnalyticsListTrackingService } from '../../../../services';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  standalone: false,
})
export class FilterComponent extends BaseComponent implements OnChanges {
  private i18NextService = inject<ITranslationService>(I18NEXT_SERVICE);
  private formBuilder = inject(FormBuilder);
  private codeService = inject(CodeService);
  private route = inject(ActivatedRoute);
  private analyticsListTracking = inject(AnalyticsListTrackingService);

  SEARCH_TYPE = Object.freeze({
    SEARCH: 'SEARCH',
    BRAND: 'BRAND',
    CATEGORY: 'CATEGORY',
  });

  FILTER_SECTION = Object.freeze({
    SORT: 'Sort',
    CATEGORY: 'Category',
    BRAND: 'Brand',
    PRICE_RANGE: 'Price Range',
    MOQ: 'MOQ',
    MOA: 'MOA',
  });

  PRICE_TYPE = Object.freeze({
    ALL: 'TERM.ALL',
    PRICE: 'TERM.PRICE',
    SUPPLY_RATE: 'TERM.SUPPLY_RATE',
  });

  ORDER_BY = Object.freeze([
    { key: 'FILTERED_PRODUCT_LIST.SORT.MATCH_SCORE', value: 'orderByMatchScore', orderBy: OrderByValue.DESC },
    { key: 'FILTERED_PRODUCT_LIST.SORT.CREATED', value: 'orderById', orderBy: OrderByValue.DESC },
    {
      key: 'FILTERED_PRODUCT_LIST.SORT.SELLING_LOWER',
      value: 'orderByBestSelling',
      orderBy: OrderByValue.DESC,
    },
    {
      key: 'FILTERED_PRODUCT_LIST.SORT.HIGHER',
      value: 'orderBySaleSupplyPrice',
      orderBy: OrderByValue.ASC,
    },
    {
      key: 'FILTERED_PRODUCT_LIST.SORT.LOWER',
      value: 'orderBySaleSupplyPrice',
      orderBy: OrderByValue.DESC,
    },
    // {
    //   key: 'FILTERED_PRODUCT_LIST.SORT.SELLING_HIGHER',
    //   value: 'orderByBestSelling',
    //   orderBy: OrderByValue.ASC,
    // },
  ]);

  form: FormGroup;

  @Input()
  filter: ProductListDto;

  @Input()
  filterType: string;

  @Input()
  pagenatedProducts: PaginatedResponse<Product>;

  @Input()
  allBrands: Brand[];

  @Input()
  allDisplayCategories: DisplayCategory[];

  // tslint:disable-next-line:no-output-on-prefix
  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  onChangeFilter = new EventEmitter<ProductListDto>();
  isSelectedAllCategory = true;
  isSelectedAllBrand = true;
  isCpnp = false;

  certificationTypes: CommonCode[];

  selected = {
    priceType: null,
    brands: null,
    displayCategoryTree: null,
  };

  constructor() {
    super();
    this.certificationTypes = this.codeService.getCommonCodes('CERTIFICATION_TYPE');
    this.form = this.formBuilder.group({
      orderBy: [],
      minPrice: [],
      maxPrice: [],
      minRate: [],
      maxRate: [],
      minMoq: [],
      maxMoq: [],
      minMoa: [],
      maxMoa: [],
      certificationType: [],
      isCpnp: false,
    });
  }

  makeOrderBy(filter: ProductListDto) {
    let orderBy: any;
    this.ORDER_BY.map((o) => {
      if (filter[o.value] && filter[o.value] === o.orderBy) {
        orderBy = o;
      }
    });
    return orderBy;
  }

  initFilterForm(filter: ProductListDto) {
    this.form = this.formBuilder.group({
      orderBy: [this.makeOrderBy(filter), [Validators.required]],
      minPrice: [filter.startSalePrice],
      maxPrice: [filter.endSalePrice],
      minRate: [filter.startSupplyRate],
      maxRate: [filter.endSupplyRate],
      minMoq: [filter.startMoq],
      maxMoq: [filter.endMoq],
      minMoa: [filter.startMoa],
      maxMoa: [filter.endMoa],
      certificationType: [filter.certificationType],
      isCpnp: [filter.isCpnp],
    });
    this.selected.priceType = this.PRICE_TYPE.ALL;

    if (filter.startSalePrice || filter.endSalePrice) {
      this.selected.priceType = this.PRICE_TYPE.PRICE;
    }

    if (filter.startSupplyRate || filter.endSupplyRate) {
      this.selected.priceType = this.PRICE_TYPE.SUPPLY_RATE;
    }

    this.filter = filter;

    if (this.selected.displayCategoryTree) {
      this.setDisplayCategory();
    }

    if (this.selected.brands) {
      this.setBrands();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.pagenatedProducts && changes.pagenatedProducts.currentValue) {
      this.pagenatedProducts = changes.pagenatedProducts.currentValue;
    }

    if (changes.filter && changes.filter.currentValue) {
      this.initFilterForm(changes.filter.currentValue);
    }

    if (changes.allBrands && changes.allBrands.currentValue) {
      this.allBrands = changes.allBrands.currentValue;
      this.selected.brands = changes.allBrands.currentValue;
      this.setBrands();
    }

    if (changes.allDisplayCategories && changes.allDisplayCategories.currentValue) {
      this.initDisplayCategory(changes.allDisplayCategories.currentValue);
    }
    for (let name in changes) {
      if (changes[name].firstChange) {
        this.onUncheckedCertificationTypeAll();
        break;
      }
    }
  }

  initDisplayCategory(displayCategories: DisplayCategory[]) {
    const allDisplayCategoryTree = this.codeService.initedDisplayCategoryAsTree(false);
    this.selected.displayCategoryTree = allDisplayCategoryTree.createSubTree(displayCategories);
    this.allDisplayCategories = displayCategories;

    this.setDisplayCategory();
  }

  setDisplayCategory() {
    this.isSelectedAllCategory = this.filter && this.filter.displayCategories.length > 0 ? false : true;

    if (this.filterType !== this.SEARCH_TYPE.CATEGORY) {
      this.selected.displayCategoryTree.selectAllChildren(this.selected.displayCategoryTree, false);
      this.filter.displayCategories.map((code) => {
        if (this.selected.displayCategoryTree.mappedCodeNode[code]) {
          this.selected.displayCategoryTree.mappedCodeNode[code].selected = true;
        }
      });
    }
  }

  setBrands() {
    this.isSelectedAllBrand = this.filter && this.filter.brandIds.length > 0 ? false : true;

    if (this.filterType !== this.SEARCH_TYPE.BRAND) {
      this.selected.brands.forEach((brand) => {
        brand.selected = false;
        this.filter.brandIds.forEach((id) => {
          if (brand.id === id) {
            brand.selected = true;
          }
        });
      });
    }
  }

  setCertificationTypes() {
    this.certificationTypes.forEach((certificationType) => {
      certificationType.selected = false;
      this.filter.certificationType.forEach((code) => {
        if (certificationType.code === code) {
          certificationType.selected = true;
        }
      });
    });
  }

  onToggleBrand(brand: Brand, $event: any) {
    this.isSelectedAllBrand = false;
    brand.selected = $event.checked;

    const selectedBrands = this.selected.brands.filter((b) => b.selected).map((b) => b.id);
    if (selectedBrands.length === 0 || selectedBrands.length === this.selected.brands.length) {
      this.isSelectedAllBrand = true;
      $event.source.checked = false;
    }
  }

  onUncheckedBrandAll() {
    if (this.filterType !== this.SEARCH_TYPE.BRAND) {
      this.selected.brands.forEach((brand) => {
        brand.selected = false;
      });
    }
  }

  onUncheckedCertificationTypeAll() {
    this.certificationTypes.forEach((certificationType) => {
      certificationType.selected = false;
    });
  }

  onUncheckedCategoryAll() {
    if (this.filterType !== this.SEARCH_TYPE.CATEGORY) {
      this.selected.displayCategoryTree.selectAllChildren(this.selected.displayCategoryTree, false);
    }
  }

  onToggleCategory(tree: Tree<DisplayCategory>, $event: any) {
    this.isSelectedAllCategory = false;
    this.selected.displayCategoryTree.selectAll(tree, $event.checked);

    const mappedNode = this.selected.displayCategoryTree.mappedNode;

    const selectedDisplayCategoryIds = Object.keys(mappedNode)
      .filter((id) => mappedNode[id].selected)
      .map((id) => mappedNode[id].item.id);

    if (
      selectedDisplayCategoryIds.length === 0 ||
      selectedDisplayCategoryIds.length === this.selected.displayCategoryTree.items.length
    ) {
      this.isSelectedAllCategory = true;
      $event.source.checked = false;
      this.selected.displayCategoryTree.selectAllChildren(this.selected.displayCategoryTree, false);
    }
  }

  initFilter() {
    this.filter.brandIds = [];
    this.filter.displayCategories = [];
    delete this.filter.startSalePrice;
    delete this.filter.endSalePrice;
    delete this.filter.startSupplyRate;
    delete this.filter.endSupplyRate;
    delete this.filter.startMoq;
    delete this.filter.endMoq;
    delete this.filter.startMoa;
    delete this.filter.endMoa;
    delete this.filter.orderById;
    delete this.filter.orderByMatchScore;
    delete this.filter.orderBySaleSupplyPrice;
    delete this.filter.orderByBestSelling;
    delete this.filter.orderByMinMoa;
    delete this.filter.certificationType;
    delete this.filter.isCpnp;
  }

  onResetForm() {
    if (this.filterType === this.SEARCH_TYPE.SEARCH) {
      this.form.get('orderBy').setValue(this.ORDER_BY[0]);
    } else if (this.filterType === this.SEARCH_TYPE.CATEGORY) {
      this.form.get('orderBy').setValue(this.ORDER_BY[1]);
    } else {
      this.form.get('orderBy').setValue(this.ORDER_BY[2]);
    }
    this.form.get('minPrice').setValue(null);
    this.form.get('maxPrice').setValue(null);
    this.form.get('minRate').setValue(null);
    this.form.get('maxRate').setValue(null);
    this.form.get('minMoq').setValue(null);
    this.form.get('maxMoq').setValue(null);
    this.form.get('minMoa').setValue(null);
    this.form.get('maxMoa').setValue(null);
    this.form.get('certificationType').setValue([]);
    this.form.get('isCpnp').setValue(false);
    this.isCpnp = false;

    this.selected.priceType = this.PRICE_TYPE.ALL;

    if (this.filterType !== this.SEARCH_TYPE.CATEGORY) {
      this.isSelectedAllCategory = true;
      // tslint:disable-next-line: no-unused-expression
      this.allDisplayCategories && this.onUncheckedCategoryAll();
    }

    if (this.filterType !== this.SEARCH_TYPE.BRAND) {
      this.isSelectedAllBrand = true;
      // tslint:disable-next-line: no-unused-expression
      this.allBrands && this.onUncheckedBrandAll();
    }
    this.onUncheckedCertificationTypeAll();
    this.onSubmitFilter();
  }

  onResetPriceRangeValue() {
    if (this.selected.priceType === this.PRICE_TYPE.ALL) {
      this.form.get('minRate').setValue(null);
      this.form.get('maxRate').setValue(null);
      this.form.get('minPrice').setValue(null);
      this.form.get('maxPrice').setValue(null);
      return;
    }

    if (this.selected.priceType === this.PRICE_TYPE.PRICE) {
      this.form.get('minRate').setValue(null);
      this.form.get('maxRate').setValue(null);
      return;
    }

    if (this.selected.priceType === this.PRICE_TYPE.SUPPLY_RATE) {
      this.form.get('minPrice').setValue(null);
      this.form.get('maxPrice').setValue(null);

      return;
    }
  }

  onSubmitFilter() {
    const formValue = this.form.value;
    this.initFilter();

    this.filter.certificationType = this.certificationTypes
      .filter((certificationType) => {
        return certificationType.selected;
      })
      .map((certificationType) => certificationType.code as CERTIFICATION_TYPE);

    this.filter.isCpnp = this.isCpnp;
    if (!this.filter.isCpnp) {
      this.filter.isCpnp = null;
    }

    if (this.filterType === this.SEARCH_TYPE.BRAND) {
      this.filter.brandIds = this.allBrands.map((brand) => brand.id);
    } else if (!this.isSelectedAllBrand) {
      const selectedBrands = this.selected.brands
        .filter((brand) => {
          return brand.selected;
        })
        .map((brand) => brand.id);
      if (selectedBrands.length !== 0 && selectedBrands.length !== this.selected.brands.length) {
        this.filter.brandIds = selectedBrands;
      }
    }

    if (this.filterType === this.SEARCH_TYPE.CATEGORY) {
      this.filter.displayCategories = this.filter.displayCategories = this.allDisplayCategories.map(
        (category) => category.code
      );
    } else if (!this.isSelectedAllCategory) {
      const mappedNode = this.selected.displayCategoryTree.mappedNode;
      const selectedDisplayCategoryIds = Object.keys(mappedNode)
        .filter((id) => mappedNode[id].selected)
        .map((id) => mappedNode[id].item.id);

      if (
        selectedDisplayCategoryIds.length !== 0 &&
        selectedDisplayCategoryIds.length !== this.selected.displayCategoryTree.items.length
      ) {
        const selected = selectedDisplayCategoryIds.map((id) => mappedNode[id].item.code);

        this.filter.displayCategories = selected;
        this.filter.displayCategories = this.filter.displayCategories;
      }
    }

    if (this.selected.priceType === this.PRICE_TYPE.PRICE) {
      if (formValue.minPrice) {
        this.filter.startSalePrice = Utils.toNumber(formValue.minPrice);
      }

      if (formValue.maxPrice) {
        this.filter.endSalePrice = Utils.toNumber(formValue.maxPrice);
      }
    } else if (this.selected.priceType === this.PRICE_TYPE.SUPPLY_RATE) {
      if (formValue.minRate) {
        this.filter.startSupplyRate = Utils.toNumber(formValue.minRate);
      }

      if (formValue.maxRate) {
        this.filter.endSupplyRate = Utils.toNumber(formValue.maxRate);
      }
    }

    if (formValue.minMoq) {
      this.filter.startMoq = Utils.toNumber(formValue.minMoq);
    }

    if (formValue.maxMoq) {
      this.filter.endMoq = Utils.toNumber(formValue.maxMoq);
    }

    if (formValue.minMoa) {
      this.filter.startMoa = Utils.toNumber(formValue.minMoa);
    }

    if (formValue.maxMoa) {
      this.filter.endMoa = Utils.toNumber(formValue.maxMoa);
    }

    if (formValue.orderBy) {
      this.filter.setSort(formValue.orderBy.value, formValue.orderBy.orderBy);
    }

    this.analyticsListTracking.trackFilterApplied(this.filter, this.allBrands ?? [], this.pagenatedProducts?.page ?? 0);
    this.onChangeFilter.emit(this.filter);
  }
}
